"use client";

import { CtaButton } from "@/component/cta-button";
import { routes } from "@/config/routes";
import { useLanguage } from "@/context/language-context";
import { useTranslations } from "@/hooks/use-translations";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import Image from "next/image";
import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";

const heroImages = [
    "/images/hero/IMG_0089.jpeg",
    "/images/hero/IMG_0053.jpeg",
    "/images/hero/IMG_0055.jpeg",
    "/images/hero/DSCF1145.JPG",
    "/images/hero/IMG_8990.jpeg",
    "/images/hero/IMG_9023.jpeg",
    "/images/hero/IMG_9181.jpeg",
    "/images/hero/IMG_9196.jpeg",
];

export default function Hero() {
    const t = useTranslations();
    const { language } = useLanguage();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isAutoPlay, setIsAutoPlay] = useState(true);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const scrollTimeoutRef = useRef<NodeJS.Timeout>(null);

    useEffect(() => {
        if (!isAutoPlay) return;

        const interval = setInterval(() => {
            setCurrentIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
        }, 5000);

        return () => clearInterval(interval);
    }, [isAutoPlay]);

    const handleDotClick = useCallback((index: number) => {
        if (index !== currentIndex) {
            setCurrentIndex(index);
        }
    }, [currentIndex]);

    const handlePrevious = useCallback(() => {
        const newIndex = (currentIndex - 1 + heroImages.length) % heroImages.length;
        setCurrentIndex(newIndex);

        // Scroll to the new image on mobile
        if (scrollContainerRef.current) {
            const itemWidth = scrollContainerRef.current.offsetWidth;
            scrollContainerRef.current.scrollTo({
                left: itemWidth * newIndex,
                behavior: 'smooth'
            });
        }
    }, [currentIndex]);

    const handleNext = useCallback(() => {
        const newIndex = (currentIndex + 1) % heroImages.length;
        setCurrentIndex(newIndex);

        // Scroll to the new image on mobile
        if (scrollContainerRef.current) {
            const itemWidth = scrollContainerRef.current.offsetWidth;
            scrollContainerRef.current.scrollTo({
                left: itemWidth * newIndex,
                behavior: 'smooth'
            });
        }
    }, [currentIndex]);

    const handleScroll = useCallback(() => {
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }

        setIsAutoPlay(false);

        if (scrollContainerRef.current) {
            const scrollLeft = scrollContainerRef.current.scrollLeft;
            const itemWidth = scrollContainerRef.current.offsetWidth;
            const newIndex = Math.round(scrollLeft / itemWidth);
            if (newIndex !== currentIndex && newIndex >= 0 && newIndex < heroImages.length) {
                setCurrentIndex(newIndex);
            }
        }

        scrollTimeoutRef.current = setTimeout(() => {
            setIsAutoPlay(true);
        }, 3000);
    }, [currentIndex]);

    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        const touch = e.touches[0];
        const startX = touch.clientX;

        const handleTouchMove = (e: TouchEvent) => {
            const currentTouch = e.touches[0];
            const deltaX = currentTouch.clientX - startX;

            if (Math.abs(deltaX) > 10) {
                handleScroll();
            }
        };

        const handleTouchEnd = () => {
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
        };

        document.addEventListener('touchmove', handleTouchMove);
        document.addEventListener('touchend', handleTouchEnd);
    }, [handleScroll]);

    if (!t) return null;

    return (
        <section className="flex flex-col desktop:flex-row w-full gap-8 items-center text-center desktop:text-left">
            <article className="flex flex-col flex-1 items-center justify-center gap-4 sm:gap-6">
                <h1 className="font-h1 flex flex-col w-full font-bold leading-[1.2] tracking-tight text-4xl sm:text-[50px]">
                    <p>{t.hero.title1}</p>
                    <p><span className="bg-gradient-to-t from-primary-600 to-primary-500 text-transparent bg-clip-text pr-1">{t.hero.title2}</span> {t.hero.title2Suffix}</p>
                </h1>

                <h2 className="max-w-2xl text-sm leading-7 text-default-600 sm:text-[18px]">
                    {t.hero.description}
                </h2>

                <div className="flex flex-col sm:flex-row desktop:items-start desktop:justify-start desktop:w-full gap-4 mt-2">
                    <CtaButton
                        as={Link}
                        href={routes.preRegistration(language)}
                        className="plausible-event-name=cta+hero"
                    >
                        {t.hero.cta}
                    </CtaButton>
                </div>
            </article>

            <div className="flex-1 pt-8 desktop:pt-0 relative w-full flex items-center justify-center">
                {/* Previous Arrow */}
                <button
                    onClick={handlePrevious}
                    className="absolute left-0 sm:left-2 top-1/2 transform -translate-y-1/2 z-30 bg-black/50 hover:bg-black/70 text-white p-1.5 sm:p-2 rounded-full transition-all duration-200 hover:scale-110"
                    aria-label="Previous image"
                >
                    <IconChevronLeft size={20} className="sm:w-6 sm:h-6" />
                </button>

                {/* Next Arrow */}
                <button
                    onClick={handleNext}
                    className="absolute right-0 sm:right-2 top-1/2 transform -translate-y-1/2 z-30 bg-black/50 hover:bg-black/70 text-white p-1.5 sm:p-2 rounded-full transition-all duration-200 hover:scale-110"
                    aria-label="Next image"
                >
                    <IconChevronRight size={20} className="sm:w-6 sm:h-6" />
                </button>

                <div className="relative w-full max-w-[600px] overflow-hidden rounded-lg" style={{ aspectRatio: '1/1' }}>

                    <div
                        ref={scrollContainerRef}
                        className="flex w-full h-full overflow-x-auto snap-x snap-mandatory scrollbar-hide md:block"
                        onScroll={handleScroll}
                        onTouchStart={handleTouchStart}
                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                    >
                        {heroImages.map((src, index) => (
                            <div
                                key={src}
                                className="relative flex-shrink-0 w-full h-full snap-center md:absolute md:inset-0"
                            >
                                <Image
                                    width={600}
                                    height={800}
                                    src={src}
                                    alt="Varese Lab space"
                                    className={`w-full h-full object-cover rounded-lg md:transition-opacity md:duration-1000 ${index === currentIndex ? "md:opacity-100" : "md:opacity-0"
                                        }`}
                                    priority={index === 0}
                                    fetchPriority={index === 0 ? "high" : "low"}
                                    sizes="(max-width: 640px) 100vw, 600px"
                                />
                            </div>
                        ))}
                    </div>
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
                        {heroImages.map((_, index) => {
                            const isActive = index === currentIndex;
                            return (
                                <button
                                    key={index}
                                    onClick={() => handleDotClick(index)}
                                    className={`w-2 h-2 rounded-full transition-all duration-300 ${isActive ? "bg-white" : "bg-white/50"
                                        }`}
                                    aria-label={`Go to image ${index + 1}`}
                                />
                            );
                        })}
                    </div>
                </div>
            </div>
        </section >
    );
}